import {
    Box,
    Button,
    Flex,
    HStack,
    Icon,
    IconButton,
    Menu,
    MenuButton,
    MenuDivider,
    MenuItem,
    MenuList,
    Text,
    Textarea,
    Tooltip,
    VStack,
} from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import ResizeTextarea from 'react-textarea-autosize';
import { TbArrowUp, TbSparkles, TbChevronDown, TbMessagePlus, TbHistory } from 'react-icons/tb';
import { Icon as YaxleIcon } from '../../../ui';
import useAskCarlChat, { isApprovalRequest } from '../../../hooks/useAskCarlChat';
import CarlLogo from '../../../components/askCarl/CarlLogo';
import CarlMessageBody, { PendingInputCard } from '../../../components/askCarl/CarlMessageBody';
import CarlFeedbackControls from '../../../components/askCarl/CarlFeedbackControls';
import CarlThinkingSparkle from '../../../components/askCarl/CarlThinkingSparkle';
import { SIDE_DRAWER_CONTAINER_PROPS, INSTANT_SIDE_DRAWER_MOTION_PROPS } from '../../../modals/sideDrawerContainerProps';
import AppDrawer from '../../../components/AppDrawer';

/**
 * Order-scoped Ask Carl drawer for the OrderDetail page. Non-modal like
 * OrderChatDrawer (no overlay, no focus trap — the page stays clickable) and
 * renders threads with the exact same message body + approval/question cards
 * as the full Ask Carl page.
 */

interface CarlChatDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    orderId: string;
    roNumber?: string;
    vehicleName?: string;
}

const CarlChatDrawer = ({ isOpen, onClose, orderId, roNumber, vehicleName }: CarlChatDrawerProps) => {
    const navigate = useNavigate();
    const [input, setInput] = useState('');
    const endRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const {
        messages,
        isLoading,
        sendMessage,
        pendingInputRequests,
        respondToInput,
        ensureOrderThread,
        startNewOrderThread,
        selectThread,
        activeThreadId,
        threads,
        submitFeedback,
    } = useAskCarlChat();

    // Bind the drawer to this order's most recent thread whenever it opens
    useEffect(() => {
        if (isOpen && orderId) {
            ensureOrderThread({ orderId, roNumber, vehicleName });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, orderId]);

    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, [messages.length, isLoading, pendingInputRequests.length]);

    const activeThread = threads.find((thread) => thread.threadId === activeThreadId);
    const isBoundToOrder = activeThread?.orderId === orderId;

    /** Every thread that referenced this order, most recent first. */
    const orderThreads = useMemo(() => (
        threads
            .filter((thread) => thread.orderId === orderId)
            .sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''))
    ), [threads, orderId]);

    /** Nothing has happened in this thread yet — show the welcome instead of a blank panel. */
    const isIdle = messages.length === 0 && pendingInputRequests.length === 0 && !isLoading;

    /** A tool approval is waiting — lock the composer until the user Approves/Denies. */
    const hasPendingApproval = pendingInputRequests.some(isApprovalRequest);
    /** Carl asked a question (e.g. "want to tweak the wording?") — typing answers it. */
    const hasPendingQuestion = !hasPendingApproval && pendingInputRequests.length > 0;

    const handleSend = async () => {
        if (!input.trim() || isLoading || !isBoundToOrder || hasPendingApproval) return;
        const next = input;
        setInput('');
        await sendMessage(next);
    };

    /** Continue this exact thread on the full Ask Carl page. */
    const openInCarl = () => {
        onClose();
        navigate('/ask-carl', activeThreadId ? { state: { askCarlThreadId: activeThreadId } } : undefined);
    };

    return (
        <AppDrawer
            isOpen={isOpen}
            onClose={onClose}
            size="md"
            hideOverlay
            title={
                <Box minW={0}>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.800" noOfLines={1}>
                        {isBoundToOrder && activeThread
                            ? activeThread.title
                            : `Ask Carl${roNumber ? ` · RO ${roNumber}` : ''}`}
                    </Text>
                    {vehicleName && (
                        <Text fontSize="xs" color="gray.500" noOfLines={1}>{vehicleName}</Text>
                    )}
                </Box>
            }
            titleAddon={<Icon as={TbSparkles as React.ElementType} boxSize={6} color="brand.solid" flexShrink={0} />}
            headerRight={
                <Tooltip label="Open in Carl" placement="bottom">
                    <IconButton
                        aria-label="Open in Carl"
                        icon={<YaxleIcon name="expand" size="sm" />}
                        size="sm"
                        variant="ghost"
                        color="fg.muted"
                        onClick={openInCarl}
                    />
                </Tooltip>
            }
            contentProps={{
                overflow: 'hidden',
                containerProps: SIDE_DRAWER_CONTAINER_PROPS,
                motionProps: INSTANT_SIDE_DRAWER_MOTION_PROPS,
            }}
            bodyProps={{ px: 3, py: 3, pt: 3, bg: 'white' }}
            bottomBar={
                <Box p={4} borderTop="1px solid" borderColor="border.default" bg="white">
                    <HStack spacing={1} mb={2}>
                        <Menu placement="top-start" isLazy>
                            <MenuButton
                                as={Button}
                                size="xs"
                                variant="ghost"
                                leftIcon={<Icon as={TbHistory as React.ElementType} w={3.5} h={3.5} />}
                                rightIcon={<Icon as={TbChevronDown as React.ElementType} w={3} h={3} />}
                                color="gray.600"
                                _hover={{ bg: 'blue.50', color: 'brand.fg' }}
                                _active={{ bg: 'blue.50', color: 'brand.fg' }}
                                borderRadius="md"
                                fontWeight="medium"
                            >
                                Threads{orderThreads.length > 1 ? ` (${orderThreads.length})` : ''}
                            </MenuButton>
                            <MenuList minW="300px" py={1} borderColor="gray.200" boxShadow="none" zIndex={20}>
                                {orderThreads.length > 0 && (
                                    <Text px={3} py={1} fontSize="xs" color="gray.400" fontWeight="semibold" letterSpacing="0.02em">
                                        This order
                                    </Text>
                                )}
                                {orderThreads.map((thread) => {
                                    const isActive = thread.threadId === activeThreadId;
                                    return (
                                        <MenuItem
                                            key={thread.threadId}
                                            py={2}
                                            onClick={() => selectThread(thread.threadId)}
                                            bg={isActive ? 'blue.50' : undefined}
                                            _hover={{ bg: isActive ? 'blue.50' : 'gray.50' }}
                                            _focus={{ bg: isActive ? 'blue.50' : 'gray.50' }}
                                        >
                                            <HStack w="100%" spacing={2}>
                                                <Box w="6px" h="6px" borderRadius="full" bg={isActive ? 'brand.solid' : 'transparent'} flexShrink={0} />
                                                <Text fontSize="sm" color="gray.800" noOfLines={1} flex={1}>
                                                    {thread.title}
                                                </Text>
                                                <Text fontSize="xs" color="gray.400" flexShrink={0}>
                                                    {moment(thread.updatedAt).fromNow(true)}
                                                </Text>
                                            </HStack>
                                        </MenuItem>
                                    );
                                })}
                                {orderThreads.length > 0 && <MenuDivider my={1} />}
                                <MenuItem
                                    py={2}
                                    onClick={() => startNewOrderThread({ orderId, roNumber, vehicleName })}
                                    _hover={{ bg: 'blue.50' }}
                                    _focus={{ bg: 'blue.50' }}
                                >
                                    <HStack spacing={2}>
                                        <Icon as={TbMessagePlus as React.ElementType} boxSize={4} color="brand.solid" />
                                        <Text fontSize="sm" color="brand.fg" fontWeight="medium">
                                            New thread for this order
                                        </Text>
                                    </HStack>
                                </MenuItem>
                            </MenuList>
                        </Menu>
                    </HStack>
                    <Box position="relative">
                        <Box
                            borderWidth="1px"
                            borderRadius="xl"
                            borderColor="gray.300"
                            bg="gray.50"
                            _hover={{ borderColor: 'gray.400' }}
                            _focusWithin={{
                                borderColor: 'brand.solid',
                                bg: 'white',
                                boxShadow: '0 0 0 3px rgba(0, 113, 227, 0.12)',
                            }}
                            transition="all 0.2s"
                            overflow="hidden"
                        >
                            <Textarea
                                ref={textareaRef}
                                as={ResizeTextarea}
                                value={input}
                                onChange={(event) => setInput(event.target.value)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' && !event.shiftKey) {
                                        event.preventDefault();
                                        handleSend();
                                    }
                                }}
                                placeholder={hasPendingApproval
                                    ? 'Approve or deny the request above…'
                                    : hasPendingQuestion
                                        ? "Reply to Carl's question above…"
                                        : `Ask about${roNumber ? ` RO ${roNumber}` : ' this order'}...`}
                                color="gray.900"
                                _placeholder={{ color: 'gray.400' }}
                                bg="transparent"
                                border="none"
                                borderRadius="0"
                                resize="none"
                                minH="80px"
                                maxRows={8}
                                // Room for the send button parked in the bottom-right corner.
                                pr="48px"
                                pb="36px"
                                overflow="auto"
                                sx={{
                                    '&::-webkit-scrollbar': { display: 'none' },
                                    msOverflowStyle: 'none',
                                    scrollbarWidth: 'none',
                                }}
                                fontSize="sm"
                                _hover={{ borderColor: 'transparent' }}
                                _focus={{ borderColor: 'transparent', boxShadow: 'none' }}
                                isDisabled={hasPendingApproval}
                            />
                        </Box>
                        <IconButton
                            aria-label="Send"
                            icon={<Icon as={TbArrowUp as React.ElementType} w={4} h={4} />}
                            position="absolute"
                            bottom="8px"
                            right="8px"
                            zIndex={2}
                            size="sm"
                            borderRadius="full"
                            bg="brand.solid"
                            color="white"
                            _hover={{ bg: 'brand.hover' }}
                            _disabled={{
                                bg: 'brand.solid',
                                opacity: 0.6,
                                cursor: 'not-allowed',
                            }}
                            onClick={handleSend}
                            isDisabled={!input.trim() || isLoading || !isBoundToOrder || hasPendingApproval}
                            minW="28px"
                            h="28px"
                        />
                    </Box>
                    {/* Standing composer footnote, same slot as the OrderChatDrawer hint. */}
                    <Text fontSize="xs" color="gray.400" mt={1.5} ml={1}>
                        Carl is AI and can make mistakes. Please double-check responses.
                    </Text>
                </Box>
            }
        >
            <VStack spacing={3} align="stretch" minH="100%">
                {isIdle && (
                    <Flex flex="1" align="center" justify="center" direction="column" py={8} px={4}>
                        <CarlLogo size="88px" welcome />
                        <Text mt={4} fontSize="md" fontWeight="semibold" color="gray.800" textAlign="center">
                            Put Carl to work on {roNumber ? `RO ${roNumber}` : 'this order'}
                        </Text>
                        <Text mt={1} fontSize="sm" color="gray.500" textAlign="center" maxW="260px">
                            Anything you can do on this order, Carl can do. Just ask.
                        </Text>
                    </Flex>
                )}

                {messages.map((message, messageIndex) => {
                    const isStreamingThis = isLoading && messageIndex === messages.length - 1;
                    const isSettled = !isStreamingThis
                        && (messageIndex !== messages.length - 1 || pendingInputRequests.length === 0);
                    return (
                        <Box
                            key={message.id}
                            alignSelf={message.role === 'user' ? 'flex-end' : 'flex-start'}
                            bg={message.role === 'user' ? 'brand.solid' : 'white'}
                            color={message.role === 'user' ? 'white' : 'gray.800'}
                            borderRadius="xl"
                            px={3}
                            py={2.5}
                            maxW={message.role === 'user' ? '85%' : '98%'}
                            w={message.role === 'assistant' && message.ui ? '98%' : undefined}
                            border={message.role === 'user' ? 'none' : '1px solid'}
                            borderColor="gray.200"
                            boxShadow={message.role === 'user' ? undefined : 'none'}
                            fontSize="sm"
                        >
                            <CarlMessageBody
                                message={message}
                                isStreaming={isStreamingThis}
                            />
                            {message.role === 'assistant' && isSettled && message.content.trim() && (
                                <CarlFeedbackControls message={message} onSubmit={submitFeedback} />
                            )}
                        </Box>
                    );
                })}

                {pendingInputRequests.map((request) => (
                    <PendingInputCard
                        key={request.requestId}
                        request={request}
                        isLoading={isLoading}
                        onRespond={respondToInput}
                    />
                ))}

                {isLoading && messages[messages.length - 1]?.role === 'user' && (
                    <HStack spacing={2} px={1}>
                        <CarlThinkingSparkle size="16px" />
                        <Text fontSize="xs" color="gray.500">Carl is working…</Text>
                    </HStack>
                )}
                <Box h="8px" />
                <div ref={endRef} />
            </VStack>
        </AppDrawer>
    );
};

export default CarlChatDrawer;
